/**
 * DelegateAssignPopover
 * -----------------------------------------------------------------------------
 * A compact, click-driven "Assign Room" popover that lives next to each delegate
 * in the HousingAssignment sidebar. Removes the need to drag every delegate to
 * the bottom of the list.
 *
 * Renders:
 *   - a small "Assign" button when the delegate has no room
 *   - a "Move" button when they already have one
 *
 * When clicked, opens a searchable list of housing_units, filtered to those
 * with free capacity. Selecting a unit invokes onAssign(unitId).
 */
import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, BedDouble, Building2, X, Check } from 'lucide-react';

const stateColor = {
  available: 'bg-green-500',
  nearly_full: 'bg-yellow-500',
  full: 'bg-red-500',
  out_of_service: 'bg-gray-500',
};

/**
 * Enrich housing_units with occupancy computed from the delegates array.
 * A delegate's `housing_unit_id` marks them as occupying that unit.
 */
export function enrichUnits(units, delegates) {
  const counts = {};
  for (const d of delegates) {
    if (d.housing_unit_id) counts[d.housing_unit_id] = (counts[d.housing_unit_id] || 0) + 1;
  }
  return units.map((u) => {
    const capacity = Number(u.capacity) || 0;
    const occupied = counts[u.id] || 0;
    const available = Math.max(0, capacity - occupied);
    let state;
    if (u.status === 'maintenance' || u.status === 'blocked') state = 'out_of_service';
    else if (available === 0) state = 'full';
    else if (capacity && available / capacity <= 0.25) state = 'nearly_full';
    else state = 'available';
    return { ...u, __occupied: occupied, __available: available, __state: state };
  });
}

const DelegateAssignPopover = ({
  delegate,
  delegates,
  units,
  delegates: allDelegates,
  onAssign,
  onUnassign,
  disabled = false,
  // Group mode: pass an array of delegate ids and a label to assign several at once
  groupIds = null,
  groupLabel = null,
  triggerLabel = null,
  triggerVariant = null,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const isGroup = Array.isArray(groupIds) && groupIds.length > 0;

  const enriched = useMemo(() => enrichUnits(units, allDelegates), [units, allDelegates]);

  const currentUnitId = isGroup ? null : delegate?.housing_unit_id;
  const currentUnit = currentUnitId ? enriched.find((u) => u.id === currentUnitId) : null;

  // In group mode, only show rooms that have enough free beds for the whole group
  const groupSize = isGroup ? groupIds.length : 1;

  // ---------------------------------------------------------------------------
  // Smart suggestions — top 3 rooms scored by:
  //   • Same group / church / company as the delegate (or as group members)   +50
  //   • Same floor as an existing delegate from the same group                +25
  //   • Same building as an existing group member                             +15
  //   • Room has just enough space (least waste)                              +10
  //   • Otherwise, most available beds wins                                    +5
  // Only computed when query is empty so search results take precedence.
  // ---------------------------------------------------------------------------
  const suggestions = useMemo(() => {
    if (q.trim()) return [];
    const source = isGroup
      ? (allDelegates || []).filter((d) => (groupIds || []).includes(d.id))
      : (delegate ? [delegate] : []);
    if (source.length === 0) return [];

    // Fingerprint the source delegates
    const groups = new Set(source.map((d) => (d.group || d.church || d.company || '').toLowerCase()).filter(Boolean));
    const roomsWithGroup = new Set();
    const buildingsWithGroup = new Set();
    const floorsWithGroup = new Set();
    for (const other of (allDelegates || [])) {
      if (!other.housing_unit_id) continue;
      const otherGroup = (other.group || other.church || other.company || '').toLowerCase();
      if (!otherGroup || !groups.has(otherGroup)) continue;
      const unit = enriched.find((u) => u.id === other.housing_unit_id);
      if (!unit) continue;
      roomsWithGroup.add(unit.id);
      if (unit.building) buildingsWithGroup.add(unit.building);
      if (unit.floor != null) floorsWithGroup.add(`${unit.building || ''}|${unit.floor}`);
    }

    const scored = enriched
      .filter((u) => u.__state !== 'out_of_service' && u.__available >= groupSize)
      .map((u) => {
        let score = 0;
        const reasons = [];
        if (roomsWithGroup.has(u.id)) { score += 50; reasons.push('same group already here'); }
        if (floorsWithGroup.has(`${u.building || ''}|${u.floor}`)) { score += 25; reasons.push('same floor as group'); }
        else if (buildingsWithGroup.has(u.building)) { score += 15; reasons.push('same building as group'); }
        // Prefer rooms that fit *just right* — least wasted space
        const waste = u.__available - groupSize;
        if (waste === 0) { score += 10; reasons.push('fits exactly'); }
        else if (waste <= 2) { score += 6; }
        // Baseline: more space, more score
        score += Math.min(5, Math.floor(u.__available / 2));
        return { unit: u, score, reasons };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

    // Only return suggestions if the top pick has *some* signal (>10)
    if (scored.length === 0 || scored[0].score < 10) return [];
    return scored;
  }, [enriched, q, isGroup, delegate, allDelegates, groupIds, groupSize]);

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    return enriched
      .filter((u) => u.__state !== 'out_of_service')
      .filter((u) => u.__available >= groupSize || (!isGroup && u.id === currentUnitId))
      .filter((u) => {
        if (!query) return true;
        return (
          (u.name || '').toLowerCase().includes(query) ||
          (u.building || '').toLowerCase().includes(query) ||
          String(u.floor || '').includes(query) ||
          (u.type || '').toLowerCase().includes(query)
        );
      })
      .sort((a, b) => (a.building || '').localeCompare(b.building || '') || (a.floor || 0) - (b.floor || 0) || (a.name || '').localeCompare(b.name || ''));
  }, [enriched, q, currentUnitId, groupSize, isGroup]);

  const handlePick = (unit) => {
    if (isGroup) {
      onAssign?.(unit.id, groupIds);
    } else {
      if (unit.id === currentUnitId) {
        setOpen(false);
        return;
      }
      onAssign?.(unit.id);
    }
    setOpen(false);
    setQ('');
  };

  const handleUnassign = () => {
    if (!currentUnitId) return;
    onUnassign?.(currentUnitId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant={triggerVariant || (currentUnit ? 'secondary' : 'default')}
          className={isGroup ? 'h-8 px-3 text-xs' : 'h-7 px-2 text-xs'}
          disabled={disabled}
          onClick={(e) => { e.stopPropagation(); }}
          data-testid={isGroup ? 'group-assign-btn' : `assign-btn-${delegate?.id}`}
        >
          {triggerLabel || (isGroup ? `Assign ${groupIds.length} together` : (currentUnit ? 'Move' : 'Assign'))}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="p-3 border-b">
          <div className="text-sm font-medium truncate">
            {isGroup
              ? (groupLabel || `Assign ${groupIds.length} delegates together`)
              : (delegate?.name || `${delegate?.first_name || ''} ${delegate?.last_name || ''}`.trim() || 'Delegate')}
          </div>
          {isGroup ? (
            <div className="text-[11px] text-slate-500 mt-1">
              Only rooms with {groupIds.length}+ free bed{groupIds.length === 1 ? '' : 's'} are shown.
            </div>
          ) : currentUnit ? (
            <div className="text-[11px] text-slate-500 mt-1 flex items-center justify-between gap-2">
              <span>Currently in <span className="font-medium">{currentUnit.name}</span></span>
              <button
                type="button"
                onClick={handleUnassign}
                className="text-red-600 hover:underline"
                data-testid={`unassign-${delegate?.id}`}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 mt-1">No room assigned</div>
          )}
        </div>

        <div className="p-2 border-b">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search room, building, floor…"
              className="pl-7 pr-6 h-8 text-xs"
              autoFocus
              data-testid={isGroup ? 'group-assign-search' : `assign-search-${delegate?.id}`}
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400"
                aria-label="Clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto">
          {/* Smart suggestions — only shown when the user hasn't typed anything */}
          {!q && suggestions.length > 0 && (
            <div className="border-b bg-purple-50/40" data-testid="assign-suggestions">
              <div className="px-3 py-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold text-purple-700">
                <Check className="w-3 h-3" />
                Suggested for you
              </div>
              <ul className="divide-y divide-purple-100">
                {suggestions.map(({ unit: u, reasons }) => (
                  <li
                    key={u.id}
                    onClick={() => handlePick(u)}
                    className="px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-purple-50"
                    data-testid={`assign-suggestion-${u.id}`}
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${stateColor[u.__state]}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{u.name}</div>
                      <div className="text-[10px] text-purple-700 truncate">
                        {reasons.slice(0, 2).join(' · ')}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {u.building || '—'} · Floor {u.floor ?? '—'} · {u.__occupied}/{u.capacity || '?'} · {u.__available} free
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {results.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500">
              <BedDouble className="w-4 h-4 mx-auto mb-1 text-slate-300" />
              No rooms available. Try a different search.
            </div>
          ) : (
            <ul className="divide-y">
              {results.map((u) => {
                const isCurrent = u.id === currentUnitId;
                return (
                  <li
                    key={u.id}
                    onClick={() => handlePick(u)}
                    className={`px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-slate-50 ${isCurrent ? 'bg-blue-50' : ''}`}
                    data-testid={`assign-room-option-${u.id}`}
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${stateColor[u.__state]}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate flex items-center gap-1">
                        {u.name}
                        {isCurrent && <Check className="w-3 h-3 text-blue-600" />}
                      </div>
                      <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-2">
                        {u.building && <span className="inline-flex items-center gap-0.5"><Building2 className="w-2.5 h-2.5" />{u.building}</span>}
                        {u.floor && <span>Floor {u.floor}</span>}
                        <span>{u.__occupied}/{u.capacity || '?'} occupied</span>
                        <span className={u.__available === 0 ? 'text-red-600' : 'text-green-700'}>{u.__available} free</span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-2 py-1 border-t text-[10px] text-slate-400 bg-slate-50 flex items-center justify-between">
          <span>{results.length} room{results.length === 1 ? '' : 's'}</span>
          <Badge variant="outline" className="text-[9px] px-1 py-0">Click to assign</Badge>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DelegateAssignPopover;
